import React, { FC, useEffect, useMemo } from 'react';

import { Form, Input, InputNumber, Modal, Select, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';

import { ISchoolDetails, ISchoolFormData } from '../../../../app/providers/redux/slices/currentSchool/types';
import { optionsRegMetki, updateDurationIntervals } from '../../../../helpers/utils/data';
import { showFormErrors } from '../../../../helpers/utils/ui';
import { validateDomain } from '../../../../helpers/utils/validators';
import { useActions } from '../../../../shared/common/hooks/useActions';
import { useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';
import { RuleObject } from 'antd/lib/form';
interface IAddModalProps {
    isVisible: boolean
    setVisible: (val: boolean) => void
    initialData: ISchoolDetails | undefined
}

const EditModal: FC<IAddModalProps> = ({ isVisible, setVisible, initialData }) => {
    const navigate = useNavigate()
    const [ form ] = Form.useForm()

    const { currentSchoolUpdate, currentSchoolSetUpdate, fetchUserAndSchoolInfo } = useActions();
    const { selectedSchool, isLoading, schoolDetail } = useTypedSelector((state) => state.currentSchool);

    function normalizeUtmSettings(utm: Record<string, string | null | undefined>): Record<string, string> {
        const result: Record<string, string> = {};
        for (const key in utm) {
            const value = utm[key];
            result[key] = value ?? '';
        }
        return result;
    }

    const editSchool = (rawValues: any) => {
        const values = normalizeUtmSettings(rawValues);
        const trimmedRawValues: Record<string, any> = {};

        for (const key in rawValues) {
            const value = rawValues[key];
            trimmedRawValues[key] = typeof value === 'string' ? value.trim() : value;
        }

        const {
            utm_origin,
            utm_source,
            utm_medium,
            utm_campaign,
            utm_content,
            utm_term,
            utm_group,
            ...rest
        } = values;

        const utm_settings = {
            utm_origin: Number(utm_origin),
            utm_source,
            utm_medium,
            utm_campaign,
            utm_content,
            utm_term,
            utm_group,
        };

        const transformedValues = {
            ...rest,
            utm_settings,
        } as ISchoolFormData;

        if (typeof transformedValues.domain === 'string') {
            transformedValues.domain = transformedValues.domain.replace(/(^\w+:|^)\/\//, '');
        }
        if (typeof transformedValues.main_domain === 'string') {
            transformedValues.main_domain = transformedValues.main_domain.replace(/(^\w+:|^)\/\//, '');
        }

        currentSchoolUpdate(
            selectedSchool?.id || '',
            transformedValues,
            () => {
                currentSchoolSetUpdate(true);
                fetchUserAndSchoolInfo(navigate);
                setVisible(false);
            },
            (errors) => {
                form.setFields(
                    Object.entries(errors).map(([name, error]) => ({
                        name,
                        errors: [error],
                    }))
                );
            }
        );
    };

    const validator = (_: RuleObject, value: string): Promise<void> => {
        const utmOrigin: number = form.getFieldValue('utm_origin');
        if ((utmOrigin === 2 || utmOrigin === 3) && (!value || (typeof value === 'string' && value.trim() === ''))) {
            return Promise.reject(new Error('Обязательное заполнение'));
        }
        return Promise.resolve();
    };
    const initialValues = useMemo(() => {
        if (!schoolDetail) return undefined;
        const { utm_settings, currency, language, ...rest } = schoolDetail;

        return {
            ...rest,
            ...utm_settings,
            currency: currency?.id || '',
            language: language?.id || '',
        };
    }, [schoolDetail]);

    return (
        <Modal
            title="Изменение информации по школе, созданной на платформе GetCourse"
            open={isVisible}
            onOk={() => form.submit()}
            onCancel={() => setVisible(false)}
            okText="Сохранить"
            cancelText="Отмена"
            okButtonProps={{ loading: isLoading }}
            destroyOnClose
        >
            <Form
                form={form}
                onFinish={editSchool}
                layout="vertical"
                requiredMark={false}
                initialValues={initialValues}
            >

                <Form.Item
                    label="Название школы"
                    name="name"
                    rules={[{ required: true, message: 'Поле обязательное' }]}
                    normalize={(value) => value?.replace(/\s+/g, ' ').trim()}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label="Валюта"
                    name="currency"
                    rules={[ { required: true, message: "Поле обязательное" } ]}
                >
                    <Select disabled>
                        <Select.Option value={1}>Рубль РФ</Select.Option>
                        <Select.Option value={2}>Доллар США</Select.Option>
                        <Select.Option value={3}>Евро</Select.Option>
                        <Select.Option value={4}>Тенге</Select.Option>
                        <Select.Option value={5}>Гривна </Select.Option>
                        <Select.Option value={6}>Беларусский рубль</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Язык интерфейса"
                    name="language"
                    normalize={(value) => value?.replace(/\s+/g, ' ').trim()}
                    rules={[{ required: true, message: 'Поле обязательное' }]}
                >
                    <Select>
                        <Select.Option value={1}>Русский</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Поддомен на Getcourse"
                    name="domain"
                    normalize={(value) => value?.replace(/\s+/g, ' ').trim()}
                    rules={[
                        {
                            required: true,
                            message: "Поле обязательное",
                        },
                        {
                            validator: validateDomain
                        }
                    ]}
                    help={form.getFieldError("domain") ? form.getFieldError("domain")[0] : ""}
                >
                    <Input placeholder="В формате name.getcourse.ru" />
                </Form.Item>

                <Form.Item
                    label="Привязанный домен к Getcourse"
                    name="main_domain"
                    normalize={(value) => value?.replace(/\s+/g, ' ').trim()}
                    rules={[
                        {
                            validator: validateDomain
                        }
                    ]}
                    help={form.getFieldError("domain") ? form.getFieldError("domain")[0] : ""}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label="Getcourse API Key"
                    name="api_key"
                    normalize={(value) => value?.replace(/\s+/g, ' ').trim()}
                    rules={[{ required: true, message: 'Поле обязательное' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label="Частота обновления данных"
                    name="update_duration"
                    normalize={(value) => value?.replace(/\s+/g, ' ').trim()}
                    rules={[{ required: true, message: 'Поле обязательное' }]}
                >
                    <Select>
                        {updateDurationIntervals.map((el) => (
                            <Select.Option value={el.id} key={el.id}>
                                {el.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Откуда брать UTM метки регистраций:"
                    name="utm_origin"
                    rules={[{ required: true, message: 'Поле обязательное' }]}
                >
                    <Select>
                        {optionsRegMetki.map((el) => (
                            <Select.Option value={el.id} key={el.id}>
                                {el.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Spin spinning={isLoading}>
                    <Form form={form} layout="vertical" onFinish={editSchool}>

                        <Form.Item
                            label="Название доп. поля для метки utm_source"
                            name="utm_source"
                            normalize={(value) => value?.replace(/\s+/g, ' ').trim()}
                            dependencies={['utm_origin']}
                            rules={[{ validator }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item name="utm_medium" label="Название доп. поля для метки utm_medium" normalize={(value) => value?.replace(/\s+/g, ' ').trim()}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="utm_campaign" label="Название доп. поля для метки utm_campaign" normalize={(value) => value?.replace(/\s+/g, ' ').trim()}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="utm_content" label="Название доп. поля для метки utm_content" normalize={(value) => value?.replace(/\s+/g, ' ').trim()}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="utm_term" label="Название доп. поля для метки utm_term" normalize={(value) => value?.replace(/\s+/g, ' ').trim()}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="utm_group" label="Название доп. поля для метки utm_group" normalize={(value) => value?.replace(/\s+/g, ' ').trim()}>
                            <Input />
                        </Form.Item>

                    </Form>
                </Spin>

            </Form>
        </Modal>
    )
}

export default EditModal
